import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ModelDistributionMetricsResponse } from '@/types/composer-dashboard';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart, Pie, Cell } from 'recharts';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import { SectionHeading } from './section-heading';
import {
  cleanModelName,
  getChartPaletteColor,
  OTHER_COLOR,
} from './helpers/model-colors';

const MIN_PERCENTAGE = 3;

interface ModelDistributionSectionProps {
  metrics: ModelDistributionMetricsResponse | undefined;
  isLoading?: boolean;
  error?: Error | null;
}

export function ModelDistributionSection({
  metrics,
  isLoading = false,
  error,
}: ModelDistributionSectionProps) {
  const pieData = useMemo(() => {
    if (!metrics) return [];

    const major: { name: string; value: number; percentage: number }[] = [];
    let otherValue = 0;
    let otherPercentage = 0;

    metrics.models.forEach((m) => {
      if (m.agent_percentage >= MIN_PERCENTAGE) {
        major.push({
          name: cleanModelName(m.model),
          value: m.agent_count,
          percentage: m.agent_percentage,
        });
      } else {
        otherValue += m.agent_count;
        otherPercentage += m.agent_percentage;
      }
    });

    major.sort((a, b) => b.value - a.value);

    if (otherValue > 0) {
      major.push({
        name: 'Other',
        value: otherValue,
        percentage: Math.round(otherPercentage * 10) / 10,
      });
    }

    // Assign colors by slice order (largest first) so this chart matches
    // the tone of "Token Usage by Model" / "Output Deliverable". "Other"
    // gets the muted gray to stay visually subordinate.
    return major.map((item, idx) => ({
      ...item,
      fill: item.name === 'Other' ? OTHER_COLOR : getChartPaletteColor(idx),
    }));
  }, [metrics]);

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    pieData.forEach((item) => {
      config[item.name] = {
        label: item.name,
        color: item.fill,
      };
    });
    return config;
  }, [pieData]);

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Card>
          <CardHeader className='pb-2'>
            <Skeleton className='h-6 w-96' />
          </CardHeader>
          <CardContent>
            <Skeleton className='h-[350px] w-full' />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !metrics || metrics.models.length === 0) {
    return (
      <div className='space-y-4'>
        <SectionHeading>Model Distribution</SectionHeading>
        <div className='flex h-[120px] items-center justify-center text-muted-foreground'>
          <p className='text-sm'>
            {error?.message || 'No model distribution data available'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <Card>
        <CardHeader className='pb-2'>
          <div className='flex items-start justify-between gap-4'>
            <CardTitle className='text-base font-semibold'>
              Model Distribution Across Current Workflow Definitions
            </CardTitle>
            <div className='text-right'>
              <div className='text-2xl font-bold leading-tight'>
                {metrics.total_agents.toLocaleString()}
              </div>
              <div className='text-xs text-muted-foreground'>total agents</div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={chartConfig}
            className='!aspect-auto h-[350px] w-full'
          >
            <PieChart>
              <Pie
                data={pieData}
                dataKey='value'
                nameKey='name'
                cx='50%'
                cy='50%'
                outerRadius={120}
                label={({ name, payload }) =>
                  `${name} (${payload?.percentage ?? 0}%)`
                }
                labelLine
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.fill} />
                ))}
              </Pie>
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value, name) => {
                      const item = pieData.find((d) => d.name === name);
                      return `${value} agents (${item?.percentage ?? 0}%)`;
                    }}
                  />
                }
              />
              <ChartLegend content={<ChartLegendContent />} />
            </PieChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}
